import { useId } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { EmptyState, StatusBadge } from '@pazportal/ui';
import { useEstimates } from '../hooks/useEstimates';
import { formatCurrency, formatSolutionType, formatDate } from '../lib/formatters';
import { ESTIMATE_STATUSES, isEstimateStatus, type EstimateListRow, type EstimateStatus } from '../lib/estimateTypes';

const STATUS_LABELS: Record<EstimateStatus, string> = {
  draft: 'Drafts',
  sent: 'Sent',
  viewed: 'Viewed',
  accepted: 'Accepted',
  rejected: 'Rejected',
  expired: 'Expired',
};

export function ProposalsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const filterId = useId();

  const rawStatus = searchParams.get('status');
  const status: EstimateStatus | undefined = isEstimateStatus(rawStatus) ? rawStatus : undefined;
  const { data: estimates, isLoading, error } = useEstimates(status ? { status } : undefined);

  const rows = estimates ?? [];

  const setStatus = (next: string) => {
    const params = new URLSearchParams(searchParams);
    if (isEstimateStatus(next)) params.set('status', next);
    else params.delete('status');
    setSearchParams(params, { replace: true });
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-text">Proposals</h1>
          <p className="text-sm text-text-muted mt-0.5">
            {status ? `${STATUS_LABELS[status]} only` : 'All estimates'}
            {!isLoading && !error ? ` · ${rows.length}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor={filterId} className="text-xs font-medium text-text-muted">Status</label>
          <select
            id={filterId}
            value={status ?? 'all'}
            onChange={(e) => setStatus(e.target.value)}
            className="min-h-11 px-3 py-2 text-sm border border-border rounded-md bg-surface text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <option value="all">All</option>
            {ESTIMATE_STATUSES.map((s) => (
              <option key={s} value={s}>{STATUS_LABELS[s]}</option>
            ))}
          </select>
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center h-64" role="status" aria-live="polite">
          <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" aria-hidden="true" />
          <span className="sr-only">Loading proposals</span>
        </div>
      )}

      {error && (
        <p className="text-sm text-error" role="alert">
          {error instanceof Error ? error.message : 'Proposals could not be loaded.'}
        </p>
      )}

      {!isLoading && !error && rows.length === 0 && (
        <div className="rounded-lg border border-border bg-surface">
          <EmptyState
            icon={<svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>}
            title={status ? `No ${STATUS_LABELS[status].toLowerCase()} estimates` : 'No estimates yet'}
            subtitle={status === 'draft' ? 'Start a new estimate to create a draft.' : 'Estimates appear here as they are created and sent.'}
            action={{ label: 'New estimate', onClick: () => navigate('/estimates/new') }}
          />
        </div>
      )}

      {!isLoading && !error && rows.length > 0 && (
        <>
          {/* Phone: one card per estimate */}
          <ul className="md:hidden space-y-2" aria-label="Proposals">
            {rows.map((e) => (
              <li key={e.id}>
                <Link
                  to={`/estimates/${e.id}`}
                  className="block rounded-lg border border-border bg-surface p-3 hover:bg-surface-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-text truncate">{e.customer?.name ?? e.contact_name ?? '—'}</p>
                      <p className="text-xs font-mono text-text-faint">{e.estimate_number}</p>
                    </div>
                    <StatusBadge status={e.status ?? 'draft'} />
                  </div>
                  <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                    <dt className="text-text-muted">Solution</dt>
                    <dd className="text-text text-right">{e.solution_type ? formatSolutionType(e.solution_type) : '—'}</dd>
                    <dt className="text-text-muted">Amount</dt>
                    <dd className="text-text text-right font-medium">{formatCurrency(e.total_cents ?? 0)}</dd>
                    <dt className="text-text-muted">Sent</dt>
                    <dd className="text-text text-right">{formatDate(e.proposal_sent_at)}</dd>
                  </dl>
                </Link>
              </li>
            ))}
          </ul>

          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-2 text-left">
                  <th scope="col" className="px-3 py-2.5">Estimate #</th>
                  <th scope="col" className="px-3 py-2.5">Client</th>
                  <th scope="col" className="px-3 py-2.5">Solution</th>
                  <th scope="col" className="px-3 py-2.5 text-right">Amount</th>
                  <th scope="col" className="px-3 py-2.5">Sent</th>
                  <th scope="col" className="px-3 py-2.5">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-divider">
                {rows.map((e: EstimateListRow) => (
                  <tr key={e.id} className="bg-surface hover:bg-surface-strong">
                    <td className="px-3 py-2.5 font-mono">
                      <Link
                        to={`/estimates/${e.id}`}
                        aria-label={`Open estimate ${e.estimate_number}`}
                        className="inline-flex min-h-11 items-center text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-sm"
                      >
                        {e.estimate_number}
                      </Link>
                    </td>
                    <td className="px-3 py-2.5 text-text">{e.customer?.name ?? e.contact_name ?? '—'}</td>
                    <td className="px-3 py-2.5 text-text-muted">{e.solution_type ? formatSolutionType(e.solution_type) : '—'}</td>
                    <td className="px-3 py-2.5 text-right font-medium text-text">{formatCurrency(e.total_cents ?? 0)}</td>
                    <td className="px-3 py-2.5 text-text-muted text-xs">{formatDate(e.proposal_sent_at)}</td>
                    <td className="px-3 py-2.5"><StatusBadge status={e.status ?? 'draft'} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
